import * as Sentry from '@sentry/react';
import AccountableLogo from 'components/atoms/AccountableLogo';
import useAuthAccountantStatus from 'hooks/Authentication/useAuthAccountantStatus';
import { useInitializeIntercom } from 'hooks/intercom.hook';
import type { FC, ReactNode } from 'react';
import { Route, Routes } from 'react-router-dom';
import PrivateRoute from 'routes/PrivateRoute';
import type { RouteType } from 'types/routing.types';

import { ExpertMsalRoot } from 'auth/ExpertMsalRoot';
import DevTools from 'components/organisms/CustomDevTools';
import OwnershipTransferBanner from 'hooks/Workspace/OwnershipTransferBanner';
import ChiftExportProvider from 'contexts/ChiftExportProvider';
import routes from './routing/routes';
import OnlyBeforeAuthRoute from 'routes/OnlyBeforeAuthRoute';

/** Only customer routes send on-behalf header; polling would fail elsewhere. */
const isCustomerRoute = (path: string) => path.startsWith('/customer/');

const SentryRoutes = Sentry.withSentryReactRouterV6Routing(Routes);

const App: FC = () => {
  const { isAuthenticated, isLoadingAuthData } = useAuthAccountantStatus();

  useInitializeIntercom();

  if (isLoadingAuthData)
    return (
      <div className="flex flex-1 h-screen items-center justify-center bg-white">
        <AccountableLogo className="w-52" />
      </div>
    );

  const wrapWithChiftProvider = (element: ReactNode, path: string) =>
    isCustomerRoute(path) ? (
      <ChiftExportProvider>{element}</ChiftExportProvider>
    ) : (
      element
    );

  return (
    <ExpertMsalRoot>
      <DevTools />
      {isAuthenticated ? <OwnershipTransferBanner /> : null}
      <SentryRoutes>
        {routes.map(
          ({
            path,
            Component,
            private: isPrivate,
            onlyBeforeAuth,
          }: RouteType): ReactNode => {
            if (isPrivate) {
              return (
                <Route
                  key={path}
                  path={path}
                  element={wrapWithChiftProvider(
                    <PrivateRoute isAuthenticated={isAuthenticated}>
                      {Component}
                    </PrivateRoute>,
                    path,
                  )}
                />
              );
            }
            if (onlyBeforeAuth) {
              return (
                <Route
                  key={path}
                  path={path}
                  element={wrapWithChiftProvider(
                    <OnlyBeforeAuthRoute isAuthenticated={isAuthenticated}>
                      {Component}
                    </OnlyBeforeAuthRoute>,
                    path,
                  )}
                />
              );
            }
            return (
              <Route
                key={path}
                path={path}
                element={wrapWithChiftProvider(Component, path)}
              />
            );
          },
        )}
      </SentryRoutes>
    </ExpertMsalRoot>
  );
};

export default App;
